import React from 'react';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragOverEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Table as TanstackTable } from '@tanstack/react-table';
import { MantineTableRow } from './MantineTableRow';
import { MantineTableFooter } from './MantineTableFooter';
import { useMinDurationLoading as useMinDurationLoader } from '@dealstack/frontend/shared/other/react/hooks/useMinDurationLoading';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Flex, Text, BoxProps } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	TableData,
	RowActionRenderer,
	DetailPanelRenderer,
	RowPropsRenderer,
	CellPropsRenderer,
	DragDropHandler,
	RowReorderHandler,
} from './types';
import { UseTableGridLayoutReturn } from './hooks/useTableGridLayout';

interface Props<T extends TableData> {
	table: TanstackTable<T>;
	gridLayout: UseTableGridLayoutReturn;
	enableRowDragAndDrop: boolean;
	enableRowSelection: boolean;
	showRowSelection: boolean;
	pinSelectionColumn: boolean;
	enableRowActions: boolean;
	enableExpanding: boolean;
	enableStickyFooter: boolean;
	hideFooterRows: boolean;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	renderDetailPanel?: DetailPanelRenderer<T>;
	mantineDetailPanelProps?: React.HTMLProps<HTMLDivElement> & BoxProps;
	mantineTableBodyRowProps?: RowPropsRenderer<T>;
	mantineTableBodyCellProps?: CellPropsRenderer<T>;
	getRowId?: (row: T, index: number, parent?: unknown) => string;
	hoveredRowId: string | null;
	setHoveredRowId: (id: string | null) => void;
	isLoading?: boolean;
	renderEmptyRowsFallback?: () => React.ReactNode;
	onRowReorder?: RowReorderHandler<T>;
	onRowDropped?: DragDropHandler<T>;
	localData: T[];
	setLocalData: (data: T[]) => void;
	enableVerticalCellBorders?: boolean;
	footerCellMinHeight?: string;
	footerCellAlignTop?: boolean;
}

const getPageKey = <T extends TableData>(table: TanstackTable<T>): string => {
	const pagination = table.getState().pagination;
	if (!pagination) {
		return 'all';
	}
	return `${pagination.pageIndex}:${pagination.pageSize}`;
};

export const MantineTableBody = <T extends TableData>({
	table,
	gridLayout,
	enableRowDragAndDrop,
	enableRowSelection,
	showRowSelection,
	pinSelectionColumn,
	enableRowActions,
	enableStickyFooter,
	enableExpanding,
	renderRowActions,
	renderRowActionMenuItems,
	renderDetailPanel,
	mantineDetailPanelProps,
	mantineTableBodyRowProps,
	mantineTableBodyCellProps,
	getRowId,
	hoveredRowId,
	setHoveredRowId,
	hideFooterRows,
	isLoading,
	renderEmptyRowsFallback,
	onRowReorder,
	onRowDropped,
	localData,
	setLocalData,
	enableVerticalCellBorders = false,
	footerCellMinHeight,
	footerCellAlignTop,
}: Props<T>) => {
	const { showLoader } = useMinDurationLoader({ isLoading });
	const tCommonUi = useTranslationCommonUi();
	const renderedPageRef = React.useRef(getPageKey(table));

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const [overId, setOverId] = React.useState<string | null>(null);

	const handleDragOver = (event: DragOverEvent) => {
		const { over } = event;
		setOverId(over?.id as string);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active } = event;
		const over = overId;

		if (!over || active.id === over) {
			setOverId(null);
			return;
		}

		const oldIndex = localData.findIndex((item) => {
			const rowId = getRowId
				? getRowId(item, localData.indexOf(item))
				: localData.indexOf(item).toString();
			return rowId === active.id;
		});

		const newIndex = localData.findIndex((item) => {
			const rowId = getRowId
				? getRowId(item, localData.indexOf(item))
				: localData.indexOf(item).toString();
			return rowId === over;
		});

		if (oldIndex === -1 || newIndex === -1) {
			setOverId(null);
			return;
		}

		const newData = arrayMove(localData, oldIndex, newIndex);
		setLocalData(newData);
		onRowReorder?.(newData);

		const movedRowId = active.id as string;
		const destinationRowId = over as string;
		const isDraggingDown = newIndex > oldIndex;
		const rowAboveId = isDraggingDown ? destinationRowId : null;
		const rowBelowId = isDraggingDown ? null : destinationRowId;

		onRowDropped?.({
			movedRowId,
			oldIndex,
			newIndex,
			rowAboveId,
			rowBelowId,
			movedRow: newData[newIndex],
			newData,
		});

		setOverId(null);
	};

	const hasFooters = table
		.getVisibleLeafColumns()
		.some((column) => (column.columnDef as { footer?: unknown }).footer);

	const hasRows = table.getRowModel().rows.length > 0;
	const showsRequestedPage = renderedPageRef.current === getPageKey(table);

	if (showLoader && (!hasRows || !showsRequestedPage)) {
		return (
			<Flex w="100%" h="100%" mih="400px" align="center" justify="center">
				<InlineLoader withoutBackground />
			</Flex>
		);
	}

	renderedPageRef.current = getPageKey(table);

	if (!hasRows) {
		return (
			<div key="empty-row" style={{ gridColumn: '1 / -1', padding: '8px' }}>
				{renderEmptyRowsFallback ? (
					renderEmptyRowsFallback()
				) : (
					<Flex align="center" justify="center" h="200px" w="100%">
						<Text c="gray.9" size="md">
							{tCommonUi('table.no_records_to_display')}
						</Text>
					</Flex>
				)}
			</div>
		);
	}

	const rows = table.getRowModel().rows.map((row, staticRowIndex) => {
		return (
			<MantineTableRow
				key={row.id}
				row={row}
				table={table}
				staticRowIndex={staticRowIndex}
				gridLayout={gridLayout}
				enableRowDragAndDrop={!!enableRowDragAndDrop}
				enableRowSelection={!!enableRowSelection}
				showRowSelection={!!showRowSelection}
				pinSelectionColumn={pinSelectionColumn}
				enableRowActions={!!enableRowActions}
				renderRowActions={renderRowActions}
				renderRowActionMenuItems={renderRowActionMenuItems}
				enableExpanding={!!enableExpanding}
				renderDetailPanel={renderDetailPanel}
				mantineDetailPanelProps={mantineDetailPanelProps}
				mantineTableBodyRowProps={mantineTableBodyRowProps}
				mantineTableBodyCellProps={mantineTableBodyCellProps}
				getRowId={getRowId}
				hoveredRowId={hoveredRowId}
				setHoveredRowId={setHoveredRowId}
				hasFooters={hasFooters}
				enableVerticalCellBorders={enableVerticalCellBorders}
			/>
		);
	});

	const footerElement = (
		<MantineTableFooter
			table={table}
			enableRowDragAndDrop={enableRowDragAndDrop}
			enableRowSelection={enableRowSelection}
			showRowSelection={showRowSelection}
			pinSelectionColumn={pinSelectionColumn}
			enableRowActions={enableRowActions}
			renderRowActions={renderRowActions}
			renderRowActionMenuItems={renderRowActionMenuItems}
			hideFooterRows={hideFooterRows}
			enableStickyFooter={enableStickyFooter}
			enableVerticalCellBorders={enableVerticalCellBorders}
			footerCellMinHeight={footerCellMinHeight}
			footerCellAlignTop={footerCellAlignTop}
		/>
	);

	const tableBodyContent = (
		<>
			{rows}
			{footerElement}
		</>
	);

	if (enableRowDragAndDrop) {
		const rowIds = table
			.getRowModel()
			.rows.map((row, index) => (getRowId ? getRowId(row.original, index) : row.id));

		return (
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
			>
				<SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
					{tableBodyContent}
				</SortableContext>
			</DndContext>
		);
	}

	return tableBodyContent;
};
